import { useMemo } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams, useSubmit } from "react-router-dom";

import UserInfo from "src/components/common/userInfo";
import ServerFooter from "src/components/common/ServerFooter";
import {
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    localWeekDays,
    outsideHolidays,
    pmEmails,
    standbyWeeks,
    statusColorCode,
} from "src/components/common/functions";
import Export from "./export";

type POTDayForm = {
    inYard?: string;
    outYard?: string;
    weekDay?: string;
    date?: string;
    yard?: string;
};

type POTWeekForm = {
    unlock?: boolean;
    status?: string;
    form?: POTDayForm[];
};

type POTPerson = {
    email: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    hireDate: string;
    quitDate?: string;
    outside?: string;
    residence?: string;
    phoneNumber?: string;
    lanId?: string;
    [week: string]: any;
};

type LoaderData = {
    forms: POTPerson[];
    week: string;
    weekEnd?: string;
};

export default function AllPlannedOT() {
    const { forms, week, weekEnd } = useLoaderData() as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const submit = useSubmit();
    const category = searchParams.get("category") ?? "";
    const status = searchParams.get("status") ?? "";
    const filterWords = searchParams.get("q") ?? "";
    const projectManagerEmails = pmEmails as string[];
    const cutoffDate = useMemo(() => (weekEnd ? new Date(weekEnd.replace(/-/g, "/")) : null), [weekEnd]);
    const filterWordsArray = useMemo(
        () => filterWords.toLowerCase().split(" ").filter(value => value.length > 0),
        [filterWords]
    );
    const visibleDays = useMemo(
        () => localWeekDays
            .map((name, index) => ({ name, index }))
            .filter((item) => {
                const date = getDatefromWeekNum(week, item.index + 4);
                return (date !== undefined && outsideHolidays(Number(week.substring(1).split("-")[0]))
                    .map((holiday) => holiday.date)
                    .includes(date)) || item.index === 1 || item.index === 2;
            }),
        [week]
    );

    const filteredForms = useMemo(() => forms.filter((form) => {
        if (projectManagerEmails.includes(form.email)) return false;
        if (!cutoffDate) return false;
        if (status && form[week]?.status !== status) return false;
        if (category && form.outside !== category) return false;
        const hireDate = new Date(form.hireDate.replace(/-/g, "/"));
        if (cutoffDate <= hireDate) return false;
        if (form.quitDate) {
            const quitDate = new Date(form.quitDate.replace(/-/g, "/"));
            if (cutoffDate > quitDate) return false;
        }
        if (filterWordsArray.length > 0) {
            const formString = JSON.stringify(Object.values(form)).toLowerCase();
            if (!filterWordsArray.every(value => formString.includes(value))) return false;
        }
        return true;
    }), [forms, cutoffDate, status, week, category, filterWordsArray, projectManagerEmails]);

    const sortedForms = useMemo(
        () => [...filteredForms].sort((a, b) => a.userName.localeCompare(b.userName)),
        [filteredForms]
    );
    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 50);
    const currentForms = useMemo(
        () => sortedForms.slice((page - 1) * limit, page * limit),
        [sortedForms, page, limit]
    );

    const handleUnlock = (form: POTPerson) => {
        const weekForm = form[week] as POTWeekForm | undefined;
        const formData = new FormData();
        formData.append("userEmail", form.email);
        formData.append("wk", week);
        formData.append("unlock", weekForm?.unlock ? "no" : "yes");
        formData.append("redirectTo", location.pathname + location.search);
        submit(formData, {
            method: "post",
            action: `/AllPlannedOT/Unlock${location.search}`,
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allPotSearch">Search</label>
                    <input
                        id = "allPotSearch"
                        className = "form-control w-auto"
                        type = "text"
                        value = {filterWords}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "q", e.currentTarget.value, true)}
                    />
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allPotWeek">Week</label>
                    <select
                        id = "allPotWeek"
                        className = "form-control w-auto"
                        value = {week}
                        onChange = {e => navigate(`/AllPlannedOT/${e.currentTarget.value}${location.search}`)}
                    >
                        {standbyWeeks.map((value, index) => (
                            <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allPotStatus">Status</label>
                    <select
                        id = "allPotStatus"
                        className = "form-control w-auto"
                        value = {status}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "status", e.currentTarget.value, true)}
                    >
                        <option value = "">All</option>
                        <option value = "Created">Unsubmitted</option>
                        <option value = "Saved">Saved</option>
                        <option value = "Submitted">Submitted</option>
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allPotCategory">Category</label>
                    <select
                        id = "allPotCategory"
                        className = "form-control w-auto"
                        value = {category}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "category", e.currentTarget.value, true)}
                    >
                        <option value = "">All</option>
                        <option value = "locator">Mark and Locator</option>
                        <option value = "qew">QEW</option>
                        <option value = "standby">Standby</option>
                    </select>
                </div>
                {status !== "Created" && filteredForms.length > 0 ? <Export filteredForms = {filteredForms} week = {week} /> : null}
            </nav>

            {!filteredForms.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <>
                    <div className = "table-responsive">
                        <table className = "table text-center tableAllBorder">
                            <thead>
                                <tr className = "stickyHeader">
                                    <th>Name</th>
                                    <th>Unlock</th>
                                    <th>Yard</th>
                                    {visibleDays.map((day) => (
                                        <th key = {day.index}>{day.name} ({getDatefromWeekNum(week, day.index + 4)})</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {currentForms.map((form) => {
                                    const weekForm = (form[week] ?? {}) as POTWeekForm;
                                    const dayForms = weekForm.form ?? [];
                                    return (
                                        <tr id = {form.email} key = {form.email} style = {{ color: statusColorCode(weekForm.status ?? "") }}>
                                            <td>
                                                <em><UserInfo name = {form.userName} id = {form.email} /></em>
                                            </td>
                                            <td>
                                                <button
                                                    type = "button"
                                                    className = {`m-1 btn btn-${weekForm.unlock ? "primary" : "info"}`}
                                                    onClick = {() => handleUnlock(form)}
                                                >
                                                    Set {weekForm.unlock ? "Lock" : "Unlock"}
                                                </button>
                                            </td>
                                            <td>{dayForms[0]?.yard ?? ""}</td>
                                            {visibleDays.map((day) => (
                                                <td key = {day.index}>
                                                    <div className = "m-2">
                                                        <em>Working OT at assigned yard:</em>
                                                        <span className = "ms-2">{dayForms[day.index]?.inYard ?? ""}</span>
                                                    </div>
                                                    <div className = "m-2">
                                                        <em>Working OT to assist another yard:</em>
                                                        <span className = "ms-2">{dayForms[day.index]?.outYard ?? ""}</span>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <ServerFooter length = {sortedForms.length} />
                </>
            )}
        </div>
    );
}

function updateSearch(
    setSearchParams: ReturnType<typeof useSearchParams>[1],
    searchParams: URLSearchParams,
    key: string,
    value: string,
    resetPage = false
) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    if (resetPage) next.set("page", "1");
    setSearchParams(next);
}
